import { OUTCOME_STATUS_META } from './outcome-status-meta';

import type { CampaignOutcomeRow, CampaignOutcomeStatus } from './campaign.schema';

/**
 * Campaign summary bar (SCRUM-72): aggregate outcome metrics for a campaign — Total plus the
 * five status counts. Counts are DERIVED from the same `outcomes` rows the table renders, so
 * the sum of the status counts always equals Total (AC #2) and the bar updates in lock-step
 * with a SCRUM-74 status change (both read the one cached campaign — plan D1/RT3). Metric
 * numbers reuse Ryan's status tokens so the strip and the badges read as one system.
 *
 * Order matches the design mockup (05-campaigns.html): Total · Interested · Acquired ·
 * Not Interested · No Response · Pending.
 */
const METRIC_ORDER: CampaignOutcomeStatus[] = [
  'Interested',
  'Acquired',
  'NotInterested',
  'NoResponse',
  'Pending',
];

function countByStatus(rows: readonly CampaignOutcomeRow[]): Record<CampaignOutcomeStatus, number> {
  const counts: Record<CampaignOutcomeStatus, number> = {
    Interested: 0,
    Acquired: 0,
    NotInterested: 0,
    NoResponse: 0,
    Pending: 0,
  };
  for (const row of rows) {
    counts[row.status] += 1;
  }
  return counts;
}

export function CampaignSummaryBar({ outcomes }: { outcomes: readonly CampaignOutcomeRow[] }) {
  const counts = countByStatus(outcomes);

  return (
    <div className="border-border-1 bg-bg-3 rounded-tk-md flex flex-wrap items-center gap-x-8 gap-y-3 border px-5 py-4">
      <Metric label="Total" value={outcomes.length} valueClass="text-text-0" />
      {METRIC_ORDER.map((status) => (
        <Metric
          key={status}
          label={OUTCOME_STATUS_META[status].label}
          value={counts[status]}
          valueClass={OUTCOME_STATUS_META[status].text}
        />
      ))}
    </div>
  );
}

function Metric({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: number;
  valueClass: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-text-3 font-mono text-[0.6875rem] font-medium tracking-[0.06em] uppercase">
        {label}
      </span>
      <span className={`text-xl font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}
